/* Public verification page for justificatif PDFs (iter50).
 * Path: /verify/justif/:token
 * Hit by anyone scanning the QR code on a justification PDF — no auth.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, XCircle, Buildings } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyJustification() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/verify/justif/${token}`);
        setState({ loading: false, data, error: '' });
      } catch (e) {
        setState({
          loading: false,
          data: null,
          error: e.response?.status === 404
            ? 'Justificatif introuvable ou déjà annulé.'
            : 'Erreur de vérification. Réessayez plus tard.',
        });
      }
    };
    load();
  }, [token]);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4 py-8" data-testid="verify-justif-page">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
        <div className="flex items-center gap-2 mb-4 text-[#0052FF]">
          <Buildings size={20} weight="duotone" />
          <span className="text-xs uppercase tracking-wide font-bold">Vérification officielle</span>
        </div>
        {state.loading ? (
          <p className="text-sm text-[#94A3B8] py-6 text-center">Vérification en cours…</p>
        ) : state.error ? (
          <div className="text-center py-4" data-testid="verify-error">
            <XCircle size={48} className="mx-auto text-red-500 mb-3" weight="duotone" />
            <h1 className="text-xl font-outfit font-bold text-red-700 mb-1">Document non reconnu</h1>
            <p className="text-sm text-[#64748B]">{state.error}</p>
          </div>
        ) : state.data ? (
          <div data-testid="verify-success">
            <div className="text-center pb-4 border-b border-[#E2E8F0]">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mb-2">
                <ShieldCheck size={40} weight="fill" />
              </div>
              <h1 className="text-2xl font-outfit font-bold text-[#10B981]">Justificatif authentique</h1>
              <p className="text-xs text-[#64748B] mt-1">
                Ce document a bien été émis par l&apos;équipe pédagogique et signé numériquement.
              </p>
            </div>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Élève</dt>
                <dd className="font-semibold text-right" data-testid="verify-student">{state.data.student_name || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Classe</dt>
                <dd className="font-mono text-right">{state.data.class_name || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Date de l&apos;absence</dt>
                <dd className="font-semibold text-right" data-testid="verify-date">{state.data.date || '—'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-[#94A3B8]">Créneau</dt>
                <dd className="text-right">{state.data.slot || '—'}</dd>
              </div>
              <div className="pt-2 border-t border-[#E2E8F0]">
                <dt className="text-[#94A3B8] text-xs uppercase tracking-wide mb-1">Motif retenu</dt>
                <dd className="text-[#0F172A] italic bg-[#F8FAFC] px-3 py-2 rounded">
                  « {state.data.reason || '—'} »
                </dd>
              </div>
              <div className="pt-2">
                <dt className="text-[#94A3B8] text-xs uppercase tracking-wide mb-1">Signé par</dt>
                <dd>
                  <p className="font-semibold">{state.data.signed_by || '—'}</p>
                  <p className="text-xs text-[#94A3B8]">{state.data.signed_role || ''}</p>
                  <p className="text-xs text-[#94A3B8]">Le {formatDate(state.data.signed_at)}</p>
                </dd>
              </div>
            </dl>
            <p className="mt-6 text-center text-[10px] text-[#94A3B8]">
              Ce document est une attestation officielle générée électroniquement.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
